import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { FC, useEffect } from 'react';
import { useController, useFormContext, useWatch } from 'react-hook-form';

import CheckboxGroup from '../forms/CheckboxGroup';
import FormGrid from '../forms/FormGrid';
import FormStack from '../forms/FormStack';
import RadioButtons from '../forms/RadioButtons';
import Select from '../forms/Select';
import TextField from '../forms/TextField';
import OptionInterface from '../../interfaces/option.interface';
import AssemblyFieldValues from '../../interfaces/assemblies/assembly-field-values.interface';
import {
  validateArrayRequired,
  validateGreaterThan,
  validateRequired,
} from '../../utils/validations';
import { DISASSEMBLY_DELIVERY_DATES } from '../../values';

const DELIVERY_DATE_OPTIONS: OptionInterface[] = [
  { label: 'Sélectionner', value: '' },
  ...DISASSEMBLY_DELIVERY_DATES,
];

const WASTE_OPTIONS: OptionInterface[] = [
  { label: 'Bois', value: 'wasteWood' },
  { label: 'Béton et/ou briques', value: 'wasteConcreteOrBricks' },
  { label: 'Gypse', value: 'wasteDrywall' },
];

const AssemblyDisassemblyForm: FC = () => {
  const { control, formState, trigger } = useFormContext<AssemblyFieldValues>();

  const hasWaste = useWatch({ name: 'hasWaste', control }) === 'true';
  const wasteOther = useWatch({ name: 'wasteOther', control });

  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController({
    name: 'wasteTypes',
    rules: validateArrayRequired(1, undefined, hasWaste && !wasteOther),
    control,
  });

  useEffect(() => {
    if (formState.isSubmitted) {
      trigger('wasteQuantity');
      trigger('wasteTypes');
    }
  }, [hasWaste, formState, trigger]);

  return (
    <FormStack>
      <FormGrid>
        <Grid item>
          <Select
            control={control}
            name="disassemblyDate"
            label={
              <>
                {'Période où vous effectuerez votre démontage. '}
                <Typography variant="body1" sx={{ mt: 1, mb: 1 }}>
                  À noter qu’aucune plage horaire ne sera attribuée pour le
                  démontage.
                </Typography>
              </>
            }
            options={DELIVERY_DATE_OPTIONS}
            rules={validateRequired()}
          />
        </Grid>

        <Grid item xs={12}>
          <RadioButtons
            control={control}
            label="Lors du démontage, prévoyez-vous laisser des rebuts&nbsp;?"
            name="hasWaste"
            options={[
              { label: 'Oui', value: 'true' },
              { label: 'Non', value: 'false' },
            ]}
            rules={validateRequired()}
            row
          />
        </Grid>

        {hasWaste && (
          <>
            <Grid item xs={12}>
              <TextField
                control={control}
                label="Quantité approximative (mètre cube)"
                name="wasteQuantity"
                rules={validateGreaterThan(0, hasWaste)}
                type="number"
                InputProps={{
                  inputProps: {
                    min: 0,
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormLabel>
                Composition
                {!!error && (
                  <FormHelperText error>{error.message}</FormHelperText>
                )}
              </FormLabel>

              <CheckboxGroup
                sx={{
                  display: 'block',
                }}
                error={error}
                onChange={(value): void => onChange(value as string[])}
                options={WASTE_OPTIONS}
                value={value}
                row
              />
            </Grid>

            <Grid item xs={12} md={8}>
              <TextField
                control={control}
                label="Autres types de rebuts"
                name="wasteOther"
                fullWidth
              />
            </Grid>
          </>
        )}
      </FormGrid>
    </FormStack>
  );
};

export default AssemblyDisassemblyForm;
